import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
} from '@angular/core';
import { Operators } from '@rivethealth/data/advanced-filters';
import {
  Filter,
  friendlyMultiNumberOperator,
  friendlySingleNumberOperator,
  negateOperator,
  negateValue,
  Operator,
} from '@rivethealth/data/super-table';
import { ObservableInput } from 'observable-input';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { FiltersComponent } from './filters.component';

@Component({
  selector: 'advanced-filter-number',
  templateUrl: './advanced-filter-number.component.html',
  styleUrls: ['./advanced-filter-number.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterNumberComponent<Name extends string> {
  constructor(
    private readonly filters: FiltersComponent<{
      [K in Name]: Filter<number[]>;
    }>,
  ) {}

  @ObservableInput()
  @Input()
  name!: Observable<Name>;

  @ObservableInput()
  @Input('multiValued')
  _multiValued!: Observable<boolean>;
  readonly multiValued = this._multiValued.pipe(startWith(false));

  @ObservableInput()
  @Input('optional')
  _optional!: Observable<boolean>;
  readonly optional = this._optional.pipe(startWith(false));

  @ObservableInput()
  @Input('negate')
  _negate!: Observable<boolean>;
  readonly negate = this._negate.pipe(startWith(false));

  @ObservableInput()
  @Input('operators')
  _operators!: Observable<Operator[]>;

  readonly defaultOperators: Observable<Operator[]> = combineLatest([
    this.multiValued,
    this.optional,
  ]).pipe(
    map(([multiValued, optional]) => {
      return multiValued
        ? [
            'NONE',
            'EMPTY',
            'NOT_EMPTY',
            'ANY_EQ',
            'ALL_EQ',
            'ANY_GTE',
            'ALL_GTE',
            'ANY_LTE',
            'ALL_LTE',
            'ANY_BETWEEN',
            'ALL_BETWEEN',
          ]
        : optional
          ? [
              'NONE',
              'EMPTY',
              'NOT_EMPTY',
              'ANY_EQ',
              'ANY_GTE',
              'ANY_LTE',
              'ANY_BETWEEN',
            ]
          : ['NONE', 'ANY_EQ', 'ANY_GTE', 'ANY_LTE', 'ANY_BETWEEN'];
    }),
  );
  readonly operators = merge(this._operators, this.defaultOperators);

  readonly operatorOptions = this.operators.pipe(
    withLatestFrom(this.multiValued),
    map(([operators, multiValued]) => {
      return operators.map(o => ({
        title: multiValued
          ? friendlyMultiNumberOperator(o)
          : friendlySingleNumberOperator(o),
        subtitle: null,
        id: o,
      }));
    }),
  );

  readonly operatorChange = new BehaviorSubject<Operator | null>(null);

  readonly queryOperator: Observable<Operator | null> = this.filters.value.pipe(
    withLatestFrom(this.name, this.negate),
    map(([value, name, negate]) =>
      name && value && value[name]
        ? negate
          ? negateOperator(value[name].operator)
          : value[name].operator
        : null,
    ),
    distinctUntilChanged(),
  );
  readonly queryFilter: Observable<number[] | null> = this.filters.value.pipe(
    withLatestFrom(this.name, this.negate),
    map(([value, name, negate]) =>
      name && value && value[name]
        ? negate
          ? negateValue(value[name].filter!)
          : value[name].filter
        : null,
    ),
    distinctUntilChanged(),
  );

  readonly queryFrom = this.queryFilter.pipe(
    map(f => {
      return f && f.length > 0 ? f[0] : null;
    }),
  );

  readonly queryTo = this.queryFilter.pipe(
    map(f => {
      return f && f.length > 1 ? f[1] : null;
    }),
  );

  readonly selectedOperator: Observable<Operator> = combineLatest([
    // Though the general strategy is to maintain separate Observables for
    // automatic values from the super table + manual changes, we loop
    // operatorChange in here because the super table ignores operator changes
    // for operators with arguments (from/to) until from/to are filled out.
    merge(
      this.queryOperator,
      this.operatorChange.pipe(filter(v => v !== null)),
    ),
    this.operators,
  ]).pipe(map(([queryOperator, operators]) => queryOperator || operators[0]));

  readonly fromChange = new BehaviorSubject<string | null>(null);
  readonly toChange = new BehaviorSubject<string | null>(null);

  isValid(v: string | null): boolean {
    return v !== null && v !== '' && !Number.isNaN(+v);
  }

  @Output()
  filterChange: Observable<Filter<number[]>> = combineLatest([
    this.operatorChange.pipe(
      map(operator => {
        if (operator === null) return null;
        // Clear out the Subject so we know it hasn't changed next time
        this.operatorChange.next(null);
        return operator;
      }),
    ),
    this.fromChange.pipe(
      debounceTime(500),
      map(from => {
        if (!this.isValid(from)) return null;
        // Clear out the Subject so we know it hasn't changed next time
        this.fromChange.next(null);
        return +from!;
      }),
    ),
    this.toChange.pipe(
      debounceTime(500),
      map(to => {
        if (!this.isValid(to)) return null;
        // Clear out the Subject so we know it hasn't changed next time
        this.toChange.next(null);
        return +to!;
      }),
    ),
  ]).pipe(
    filter(changes => changes.some(change => change !== null)),
    withLatestFrom(
      this.selectedOperator,
      merge(
        this.queryFrom,
        this.fromChange.pipe(
          filter(v => this.isValid(v)),
          map(v => +v!),
        ),
      ),
      merge(
        this.queryTo,
        this.toChange.pipe(
          filter(v => this.isValid(v)),
          map(v => +v!),
        ),
      ),
      this.negate,
    ),
    map(
      ([
        [operatorChange, fromChange, toChange],
        operator,
        queryFrom,
        queryTo,
        negate,
      ]) => {
        return [
          operatorChange ?? operator,
          fromChange ?? queryFrom,
          toChange ?? queryTo,
          negate,
        ] as const;
      },
    ),
    filter(([op, from, to]) => {
      const a = Operators.arity(op);
      return (
        a === 'NULLARY' ||
        (a === 'UNARY' && from !== null) ||
        (a === 'BINARY' && to !== null && from !== null)
      );
    }),
    map(([operator, from, to, negate]) => {
      const a = Operators.arity(operator);
      const filter =
        a === 'NULLARY' ? [] : a === 'UNARY' ? [from!] : [from!, to!];
      return {
        operator: negate ? negateOperator(operator) : operator,
        filter: negate ? negateValue(filter) : filter,
      };
    }),
    withLatestFrom(this.name),
    tap(([filter, name]) => {
      this.filters.changeValue.next({ name, reduce: () => filter });
    }),
    map(([filter]) => filter),
  );

  showFrom(operator: Operator) {
    const a = Operators.arity(operator);
    return a === 'UNARY' || a === 'BINARY';
  }
  showTo(operator: Operator) {
    const a = Operators.arity(operator);
    return a === 'BINARY';
  }
}
